import type { ClientMessage, ServerMessage, GameState, Tower, Creep, PlayerState, TowerType, SendUnitType } from '@shared/types'

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error'

export interface NetworkEvents {
  onStateChange: (state: ConnectionState) => void
  onGameState: (state: GameState) => void
  onPlayerJoined: (player: PlayerState) => void
  onTowerPlaced: (playerId: string, tower: Tower) => void
  onTowerRejected: (reason: string) => void
  onUnitSent: (playerId: string, unitType: SendUnitType) => void
  onCreepSpawned: (creep: Creep) => void
  onCreepUpdate: (creepId: string, x: number, y: number, hp: number) => void
  onCreepDied: (creepId: string, killerId: string, bounty: number) => void
  onCreepEscaped: (creepId: string, laneId: string) => void
  onWaveStart: (wave: number, buildTime: number) => void
  onWaveEnd: (wave: number) => void
  onBuildPhase: (timeRemaining: number) => void
  onGoldUpdate: (playerId: string, gold: number, income: number) => void
  onLivesUpdate: (playerId: string, lives: number) => void
  onGameOver: (winnerId: string) => void
  onError: (message: string) => void
}

export class NetworkClient {
  private ws: WebSocket | null = null
  private state: ConnectionState = 'disconnected'
  private events: Partial<NetworkEvents> = {}
  private serverUrl: string
  private roomId: string = ''
  private reconnectAttempts: number = 0
  private maxReconnectAttempts: number = 3

  constructor(serverUrl: string) {
    this.serverUrl = serverUrl
  }

  on<K extends keyof NetworkEvents>(event: K, callback: NetworkEvents[K]) {
    this.events[event] = callback
  }

  off<K extends keyof NetworkEvents>(event: K) {
    delete this.events[event]
  }

  clearAllHandlers() {
    this.events = {}
  }

  getState(): ConnectionState {
    return this.state
  }

  getRoomId(): string {
    return this.roomId
  }

  async connect(roomId: string): Promise<boolean> {
    this.roomId = roomId
    this.setState('connecting')

    return new Promise((resolve) => {
      try {
        // Connect to the Durable Object
        const wsUrl = `${this.serverUrl}/room/${roomId}`
        this.ws = new WebSocket(wsUrl)

        this.ws.onopen = () => {
          this.setState('connected')
          this.reconnectAttempts = 0
          resolve(true)
        }

        this.ws.onmessage = (event) => {
          try {
            const message: ServerMessage = JSON.parse(event.data)
            this.handleMessage(message)
          } catch (e) {
            console.error('Failed to parse server message:', e)
          }
        }

        this.ws.onclose = () => {
          this.setState('disconnected')
          this.tryReconnect()
        }

        this.ws.onerror = () => {
          this.setState('error')
          resolve(false)
        }
      } catch (e) {
        this.setState('error')
        resolve(false)
      }
    })
  }

  disconnect() {
    this.reconnectAttempts = this.maxReconnectAttempts // Prevent reconnection
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.setState('disconnected')
  }

  private tryReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.roomId) {
      this.reconnectAttempts++
      setTimeout(() => {
        this.connect(this.roomId)
      }, 1000 * this.reconnectAttempts)
    }
  }

  private setState(state: ConnectionState) {
    this.state = state
    this.events.onStateChange?.(state)
  }

  private handleMessage(message: ServerMessage) {
    switch (message.type) {
      case 'game_state':
        this.events.onGameState?.(message.state)
        break
      case 'player_joined':
        this.events.onPlayerJoined?.(message.player)
        break
      case 'tower_placed':
        this.events.onTowerPlaced?.(message.playerId, message.tower)
        break
      case 'tower_rejected':
        this.events.onTowerRejected?.(message.reason)
        break
      case 'unit_sent':
        this.events.onUnitSent?.(message.playerId, message.unitType)
        break
      case 'creep_spawned':
        this.events.onCreepSpawned?.(message.creep)
        break
      case 'creep_update':
        this.events.onCreepUpdate?.(message.creepId, message.x, message.y, message.hp)
        break
      case 'creep_died':
        this.events.onCreepDied?.(message.creepId, message.killerId, message.bounty)
        break
      case 'creep_escaped':
        this.events.onCreepEscaped?.(message.creepId, message.laneId)
        break
      case 'wave_start':
        this.events.onWaveStart?.(message.wave, message.buildTime)
        break
      case 'wave_end':
        this.events.onWaveEnd?.(message.wave)
        break
      case 'build_phase':
        this.events.onBuildPhase?.(message.timeRemaining)
        break
      case 'gold_update':
        this.events.onGoldUpdate?.(message.playerId, message.gold, message.income)
        break
      case 'lives_update':
        this.events.onLivesUpdate?.(message.playerId, message.lives)
        break
      case 'game_over':
        this.events.onGameOver?.(message.winnerId)
        break
      case 'error':
        this.events.onError?.(message.message)
        break
    }
  }

  private send(message: ClientMessage) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message))
    }
  }

  // Client actions
  join(playerName: string) {
    this.send({ type: 'join', playerName })
  }

  placeTower(towerType: TowerType, x: number, y: number) {
    this.send({ type: 'place_tower', towerType, x, y })
  }

  sendUnit(unitType: SendUnitType) {
    this.send({ type: 'send_unit', unitType })
  }

  ready() {
    this.send({ type: 'ready' })
  }
}

// Singleton instance
let networkClient: NetworkClient | null = null

export function getNetworkClient(serverUrl?: string): NetworkClient {
  if (!networkClient && serverUrl) {
    networkClient = new NetworkClient(serverUrl)
  }
  if (!networkClient) {
    throw new Error('NetworkClient not initialized. Call with serverUrl first.')
  }
  return networkClient
}
